// src/utils/pageLeaveGuard.js
// Prompt before leaving a page only when in-memory work would be lost.

export const PAGE_LEAVE_DISMISS_KEY = 'metaPtcg.pageLeaveGuard.dismissed';

const dirtySources = new Map();
let allowNextUnload = false;

function readDismissed() {
    try {
        return window.localStorage.getItem(PAGE_LEAVE_DISMISS_KEY) === '1';
    } catch {
        return false;
    }
}

function writeDismissed() {
    try {
        window.localStorage.setItem(PAGE_LEAVE_DISMISS_KEY, '1');
    } catch {
        // private mode / quota
    }
}

export function normalizeAppPath(pathname = window.location.pathname || '/') {
    const path = String(pathname || '/').replace(/\/+$/, '') || '/';
    return path;
}

export function isSameAppPath(href) {
    try {
        const next = new URL(href, window.location.origin);
        if (next.origin !== window.location.origin) return false;
        return normalizeAppPath(next.pathname) === normalizeAppPath(window.location.pathname);
    } catch {
        return normalizeAppPath(href) === normalizeAppPath();
    }
}

export function setPageDirty(source, isDirty, reason = '') {
    if (!source) return;
    if (isDirty) {
        dirtySources.set(source, String(reason || 'Unsaved work on this page is only in memory.'));
    } else {
        dirtySources.delete(source);
    }
}

export function clearPageDirty(source) {
    if (source) dirtySources.delete(source);
}

export function getDirtyReasons() {
    return [...new Set([...dirtySources.values()].filter(Boolean))];
}

export function isAnyPageDirty() {
    return dirtySources.size > 0;
}

export function isLeavePromptDismissed() {
    return readDismissed();
}

export function dismissLeavePromptForever() {
    writeDismissed();
}

export function resetLeavePromptDismiss() {
    try {
        window.localStorage.removeItem(PAGE_LEAVE_DISMISS_KEY);
    } catch {
        // private mode / quota
    }
}

export function isHomepagePath(pathname = window.location.pathname) {
    return normalizeAppPath(pathname) === '/';
}

export function shouldPromptOnLeave() {
    return isAnyPageDirty() && !isLeavePromptDismissed();
}

/** Native reload/close dialogs cannot include a checkbox. Skip them on the homepage (decks persist) and when dismissed. */
export function shouldUseNativeUnloadPrompt() {
    if (isLeavePromptDismissed() || isHomepagePath()) return false;
    return isAnyPageDirty();
}

export function allowNextPageUnload() {
    allowNextUnload = true;
}

export function consumeAllowNextUnload() {
    if (!allowNextUnload) return false;
    allowNextUnload = false;
    return true;
}

export function getPageLeaveDebugState() {
    return {
        dirty: isAnyPageDirty(),
        reasons: getDirtyReasons(),
        dismissed: isLeavePromptDismissed(),
        homepage: isHomepagePath(),
        nativeUnload: shouldUseNativeUnloadPrompt(),
        sources: [...dirtySources.keys()]
    };
}
